// banking/src/pages/onboarding/corporate/components/FileUploader.tsx

import React, { useRef, useState } from 'react';
import { getTranslation, type Language } from '../corporate-translations';

interface UploadedFileMetadata {
  name: string;
  url: string;
  _isUploaded: true;
}

interface FileUploaderProps {
  value?: File | UploadedFileMetadata;
  onChange: (file: File | undefined) => void;
  onFileSelected?: (file: File) => Promise<void>; // NEW: Upload immediately
  language: Language;
  error?: string;
  label?: string;
  required?: boolean;
  accept?: string;
  maxSizeMB?: number;
  uploading?: boolean; // NEW: Show uploading state
}

const FileUploader: React.FC<FileUploaderProps> = ({
  value,
  onChange,
  onFileSelected,
  language,
  error,
  label,
  required = false,
  accept = '.pdf,.jpg,.jpeg,.png',
  maxSizeMB = 10,
  uploading = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSizeBytes) {
      return `File size must be less than ${maxSizeMB}MB`;
    }

    // Check file type
    const allowedTypes = accept.split(',').map(t => t.trim().toLowerCase());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const fileMimeType = file.type.toLowerCase();

    const isValidExtension = allowedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExtension === type;
      }
      return fileMimeType.startsWith(type.replace('*', ''));
    });

    if (!isValidExtension) {
      return `Invalid file type. Allowed: ${accept}`;
    }

    return null;
  };

  const handleFile = async (file: File) => {
    setUploadError('');

    const validationError = validateFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    onChange(file);

    // If onFileSelected callback is provided, upload immediately
    if (onFileSelected) {
      setIsUploading(true);
      try {
        await onFileSelected(file);
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadError('Error uploading file. Please try again.');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleRemove = () => {
    onChange(undefined);
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const displayError = error || uploadError;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload area or file preview */}
      {value ? (
        // File uploaded - show preview
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {/* File icon */}
              <div className="flex-shrink-0">
                {value instanceof File && value.type.startsWith('image/') ? (
                  <div className="w-12 h-12 rounded overflow-hidden bg-gray-200">
                    <img
                      src={URL.createObjectURL(value)}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-[#B3A369]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {value.name}
                </p>
                {value instanceof File ? (
                  <p className="text-xs text-gray-500">
                    {formatFileSize(value.size)}
                  </p>
                ) : (
                  <p className="text-xs text-blue-600">
                    {language === 'es' ? 'Archivo previamente subido' :
                     language === 'fr' ? 'Fichier déjà téléchargé' :
                     language === 'de' ? 'Bereits hochgeladene Datei' :
                     language === 'it' ? 'File già caricato' :
                     language === 'pt' ? 'Arquivo já enviado' :
                     language === 'nl' ? 'Bestand al geüpload' :
                     language === 'ru' ? 'Файл уже загружен' :
                     language === 'ar' ? 'الملف محمّل مسبقاً' :
                     'Previously uploaded file'}
                  </p>
                )}
              </div>

              {/* View/Remove buttons */}
              <div className="flex items-center space-x-2">
                {/* View button (only for uploaded files with URL) */}
                {!(value instanceof File) && 'url' in value && (
                  <a
                    href={value.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 text-[#B3A369] hover:text-[#9A8B57] transition-colors"
                    title={language === 'es' ? 'Ver archivo' :
                           language === 'fr' ? 'Voir le fichier' :
                           language === 'de' ? 'Datei ansehen' :
                           language === 'it' ? 'Visualizza file' :
                           language === 'pt' ? 'Ver arquivo' :
                           language === 'nl' ? 'Bestand bekijken' :
                           language === 'ru' ? 'Просмотреть файл' :
                           language === 'ar' ? 'عرض الملف' :
                           'View file'}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </a>
                )}

                {/* Remove button */}
                <button
                  type="button"
                  onClick={handleRemove}
                  className="flex-shrink-0 text-red-600 hover:text-red-700 transition-colors"
                  title={getTranslation('remove_file', language)}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Success/Uploading indicator */}
          {isUploading || uploading ? (
            <div className="mt-2 flex items-center text-blue-600 text-sm">
              <svg
                className="w-4 h-4 mr-1 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {language === 'es' ? 'Subiendo a S3...' :
               language === 'fr' ? 'Téléchargement vers S3...' :
               language === 'de' ? 'Upload zu S3...' :
               language === 'it' ? 'Caricamento su S3...' :
               language === 'pt' ? 'Enviando para S3...' :
               language === 'nl' ? 'Uploaden naar S3...' :
               language === 'ru' ? 'Загрузка на S3...' :
               language === 'ar' ? 'جاري الرفع إلى S3...' :
               'Uploading to S3...'}
            </div>
          ) : (
            <div className="mt-2 flex items-center text-green-600 text-sm">
              <svg
                className="w-4 h-4 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {value instanceof File
                ? getTranslation('file_uploaded', language)
                : (language === 'es' ? 'Archivo guardado en el sistema' :
                   language === 'fr' ? 'Fichier enregistré dans le système' :
                   language === 'de' ? 'Datei im System gespeichert' :
                   language === 'it' ? 'File salvato nel sistema' :
                   language === 'pt' ? 'Arquivo salvo no sistema' :
                   language === 'nl' ? 'Bestand opgeslagen in systeem' :
                   language === 'ru' ? 'Файл сохранен в системе' :
                   language === 'ar' ? 'الملف محفوظ في النظام' :
                   'File saved in system')
              }
            </div>
          )}
        </div>
      ) : (
        // No file - show upload area
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-colors
            ${isDragging
              ? 'border-#B3A369] bg-[#F5F3E8]'
              : displayError
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 bg-gray-50 hover:border-#B3A369] hover:bg-[#F5F3E8]'
            }
          `}
        >
          {/* Upload icon */}
          <svg
            className={`mx-auto h-12 w-12 ${
              displayError ? 'text-red-400' : 'text-gray-400'
            }`}
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* Text */}
          <p className="mt-4 text-sm text-gray-600">
            {getTranslation('drag_drop_text', language)}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {accept} (max {maxSizeMB}MB)
          </p>

          {/* Upload button */}
          <button
            type="button"
            className="mt-4 px-4 py-2 bg-[#B3A369] text-white text-sm font-medium rounded-lg hover:bg-[#9A8B57] transition-colors"
          >
            {getTranslation('upload_file_button', language)}
          </button>
        </div>
      )}

      {/* Error message */}
      {displayError && (
        <p className="mt-2 text-sm text-red-600">{displayError}</p>
      )}
    </div>
  );
};

export default FileUploader;
